import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

import { LoginOtpInput } from '@/components/login/LoginOtpInput';
import { confirmOtpSms } from '@/graphql/hasura/auth';
import { processAuth0Token } from '@/lib/auth_v2';
import { AlterError } from '@/lib/error';
import { sendSentryError } from '@/lib/sentry/sentry';
import { ErrorCode } from '@/models/error-codes';
import type { AlterPasswordlessCodeFormData } from '@/models/login.model';
import { LoginStage } from '@/models/login.model';
import { sendPasswordlessLoginAnalytics } from '@/utils/analytics';
import { _getPath } from '@/utils/helpers/get-path';

interface LoginPasswordlessConfirmProps {
  setLoginStage: (stage: LoginStage) => void;
  phone: string | null;
  className?: string;
}

export const LoginPasswordlessConfirm: React.FC<LoginPasswordlessConfirmProps> = ({
  setLoginStage,
  phone,
  className,
}) => {
  const searchParams = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [confirmationError, setConfirmationError] = useState(false);
  const router = useRouter();
  const { query } = router;

  const _handleLoginSuccess = useCallback(
    (phone: string) => {
      sendPasswordlessLoginAnalytics(phone);
      localStorage.removeItem('kitId');
      localStorage.removeItem('activation_code');
      const from = searchParams.get('from') ?? '/portal';
      const [fromPath, search] = from.split('?');
      const path = _getPath(query, fromPath);
      return router.push({
        pathname: path,
        search: search ?? '',
      });
    },
    [query, router, searchParams]
  );

  const onSubmit = async (data: AlterPasswordlessCodeFormData) => {
    setLoading(true);

    if (!phone) {
      throw new Error('PHONE_NOT_DEFINED');
    }

    try {
      setLoginStage(LoginStage.passwordLessConfirm);
      const tokenSet = await confirmOtpSms({ phone, otp: data.otp });
      processAuth0Token(tokenSet);
      _handleLoginSuccess(phone);
    } catch (error) {
      if (error instanceof AlterError) {
        switch (error.errorCode) {
          case ErrorCode.errorConfirmingOtpSmsForPhone:
            setConfirmationError(true);
            return;
        }
        sendSentryError('UNKNOWN_ALTER_ERROR', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AlterPasswordlessCodeFormData>({});

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="min-h-[152px]">
        <div className="flex flex-col gap-[16px] mt-6 break-words lg:mt-10">
          <LoginOtpInput control={control} errors={errors}></LoginOtpInput>
        </div>
        <div className="h-20">
          <div className="pt-5 px-6 text-sm text-alter-black-80 font-light">
            Enter the six-digit code that was just sent to you
          </div>
          {confirmationError && (
            <div className="mt-3 text-sm text-alter-error">Wrong Verification Code</div>
          )}
        </div>
      </div>
      <button
        type="submit"
        data-testid="loginButton"
        className="button w-full mt-6 mb-2 font-light text-lg"
      >
        {loading ? 'Loading' : 'Confirm'}
      </button>
    </form>
  );
};
